import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { form, required } from '@angular/forms/signals';
import { MatIconModule } from '@angular/material/icon';
import { PropertyCountry, PropertyGeolocationType, PropertyLocationDto } from '@workspace/data-types';
import {
  AbstractSignalFormComponent,
  AddressSearchInputComponent,
  OpenStreetMapComponent,
} from '@workspace/ng-aura';
import { ApiService } from '@workspace/ng-http';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { PropertyDataCleaner } from '../../../utils/property-data.cleaner';
import { PropertyLocationModel } from '../../property-location';

@Component({
  selector: 'etxe-property-location-step',
  standalone: true,
  imports: [CommonModule, MatIconModule, OpenStreetMapComponent, AddressSearchInputComponent],
  templateUrl: './property-location-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyLocationStepComponent
  extends AbstractSignalFormComponent<PropertyLocationDto, PropertyLocationModel>
  implements OnInit, OnDestroy
{
  private api = inject(ApiService);

  isLocked = input<boolean>(false);

  searchQuery = signal<string>('');
  suggestions = signal<Record<string, unknown>[]>([]);
  isLoading = signal<boolean>(false);
  radius = signal<number>(400);
  private searchSubject = new Subject<string>();
  private searchSubscription: Subscription | undefined;

  displayOptions = [
    {
      label: 'Quartier',
      value: PropertyGeolocationType.NEIGHBORHOOD,
      radius: 400,
      description: 'Affiche un rayon restreint (~400 m) autour du bien.',
    },
    {
      label: 'Ville',
      value: PropertyGeolocationType.CITY,
      radius: 2000,
      description: 'Affiche le périmètre élargi de la commune (~2 km).',
    },
    {
      label: 'Région',
      value: PropertyGeolocationType.REGION,
      radius: 10000,
      description: 'Affiche uniquement la zone régionale (~10 km).',
    },
  ];

  geolocationTypeLabel = computed(() => {
    const opt = this.displayOptions.find((o) => o.value === this.model()?.geolocationType);
    return opt?.label || 'Quartier';
  });

  isCustomLocationActive = signal<boolean>(false);

  override model: WritableSignal<PropertyLocationModel> = signal<PropertyLocationModel>({
    address: {
      street: '',
      city: '',
      zipCode: '',
      country: PropertyCountry.FR,
      complement: '',
      district: '',
      houseNumber: '',
      label: '',
      region: '',
      cityCode: '',
    },
    coordinates: {
      latitude: null,
      longitude: null,
    },
    geolocationType: PropertyGeolocationType.NEIGHBORHOOD,
  });

  override formTree = form(this.model, (b) => {
    required(b.address.street);
    required(b.address.city);
    required(b.address.zipCode);
    required(b.coordinates.latitude);
    required(b.coordinates.longitude);
  });

  ngOnInit() {
    const dto = this.dto();
    if (dto?.address?.street) {
      this.searchQuery.set(dto.address.street);
    }
    if (dto?.geolocationType) {
      this.radius.set(this.setRadius(dto.geolocationType));
    }

    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => this.isLoading.set(true)),
        switchMap((query: string) => {
          if (!query || query.length < 3) {
            this.isLoading.set(false);
            return of([]);
          }
          return this.api
            .public()
            .address()
            .search(query as string)
            .pipe(catchError(() => of([])));
        }),
      )
      .subscribe((results: unknown) => {
        const resObj = results as { features?: Record<string, unknown>[] };
        this.suggestions.set(resObj?.features || []);
        this.isLoading.set(false);
      });
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  onSearchChange(query: string) {
    this.searchQuery.set(query);
    this.searchSubject.next(query);
  }

  clearSearch() {
    this.searchQuery.set('');
    this.suggestions.set([]);
    this.model.update((prev) => ({
      ...prev,
      address: {
        street: '',
        city: '',
        zipCode: '',
        country: PropertyCountry.FR,
        complement: '',
        district: '',
        houseNumber: '',
        label: '',
        region: '',
        cityCode: '',
      },
      coordinates: {
        latitude: null,
        longitude: null,
      },
    }));
  }

  selectAddress(feature: unknown) {
    const featObj = (feature || {}) as Record<string, unknown>;
    const props = (featObj['properties'] ?? {}) as Record<string, unknown>;
    const geometry = (featObj['geometry'] ?? {}) as Record<string, unknown>;
    const coords = (geometry['coordinates'] ?? []) as number[];

    const geolocationType = this.model().geolocationType || PropertyGeolocationType.NEIGHBORHOOD;

    this.model.update((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        street: props['name'] as string,
        city: props['city'] as string,
        zipCode: props['postcode'] as string,
        district: props['district'] as string,
        houseNumber: props['housenumber'] as string,
        label: props['label'] as string,
        region: (props['context'] as string)?.split(',').pop()?.trim() || '',
        cityCode: props['citycode'] as string,
        country: PropertyCountry.FR,
      },
      coordinates: {
        latitude: coords[1] ?? null,
        longitude: coords[0] ?? null,
      },
      geolocationType,
    }));

    this.searchQuery.set(props['label'] as string);
    this.suggestions.set([]);
  }

  setDisplayType(type: PropertyGeolocationType) {
    this.model.update((m) => ({ ...m, geolocationType: type }));
    this.radius.set(this.setRadius(type));
  }

  setRadius(type: PropertyGeolocationType) {
    switch (type) {
      case PropertyGeolocationType.NEIGHBORHOOD:
        return 800;
      case PropertyGeolocationType.CITY:
        return 2000;
      case PropertyGeolocationType.REGION:
        return 10000;
      default:
        return 800;
    }
  }

  protected override buildModelFromDto(dto: PropertyLocationDto): PropertyLocationModel {
    return {
      address: {
        street: dto?.address?.street ?? '',
        city: dto?.address?.city ?? '',
        zipCode: dto?.address?.zipCode ?? '',
        country: dto?.address?.country ?? PropertyCountry.FR,
        complement: dto?.address?.complement ?? '',
        district: dto?.address?.district ?? '',
        houseNumber: dto?.address?.houseNumber ?? '',
        label: dto?.address?.label ?? '',
        region: dto?.address?.region ?? '',
        cityCode: dto?.address?.cityCode ?? '',
      },
      coordinates: {
        latitude: dto?.coordinates?.latitude ?? null,
        longitude: dto?.coordinates?.longitude ?? null,
      },
      geolocationType: dto?.geolocationType ?? PropertyGeolocationType.NEIGHBORHOOD,
    };
  }

  protected override buildDtoFromModel(model: PropertyLocationModel): PropertyLocationDto {
    const rawDto: PropertyLocationDto = {
      ...(this.dto() || ({} as PropertyLocationDto)),
      address: {
        street: model.address.street,
        city: model.address.city,
        zipCode: model.address.zipCode,
        country: model.address.country,
        complement: model.address.complement || undefined,
        district: model.address.district || undefined,
        houseNumber: model.address.houseNumber || undefined,
        label: model.address.label || undefined,
        region: model.address.region || undefined,
        cityCode: model.address.cityCode || undefined,
      },
      coordinates: {
        latitude: model.coordinates.latitude ?? 0,
        longitude: model.coordinates.longitude ?? 0,
      },
      geolocationType: model.geolocationType ?? PropertyGeolocationType.NEIGHBORHOOD,
    };

    return PropertyDataCleaner.deepClean(rawDto) as PropertyLocationDto;
  }
}
